import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import AminiiNavbar from "../components/AminiiNavbar";
import AminiiFooter from "../components/AminiiFooter";
import "../css/AminiiTrackOrder.css";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  "https://aminii-react-prod.onrender.com/api";

const STATUS_STEPS = [
  { key: "ORDER_PLACED",      label: "Order Placed",      icon: "📋", desc: "We received your order and it's being reviewed." },
  { key: "ORDER_CONFIRMED",   label: "Confirmed",         icon: "✅", desc: "Your order has been confirmed and queued for packing." },
  { key: "READY_TO_SHIP",     label: "Ready to Ship",     icon: "📦", desc: "Your items are packed and awaiting courier pickup." },
  { key: "IN_TRANSIT",        label: "In Transit",        icon: "🚚", desc: "Your package is on its way to you." },
  { key: "OUT_FOR_DELIVERY",  label: "Out for Delivery",  icon: "🛵", desc: "Your package is out for delivery today!" },
  { key: "DELIVERED",         label: "Delivered",         icon: "🎉", desc: "Your order has been delivered. Enjoy!" },
];

const STATUS_COLORS = {
  ORDER_PLACED:     { bg: "#e0f2fe", color: "#0369a1" },
  PENDING:          { bg: "#fef3c7", color: "#92400e" },
  ORDER_CONFIRMED:  { bg: "#e0f2fe", color: "#0369a1" },
  READY_TO_SHIP:    { bg: "#ede9fe", color: "#5b21b6" },
  IN_TRANSIT:       { bg: "#e0e7ff", color: "#3730a3" },
  OUT_FOR_DELIVERY: { bg: "#fef9c3", color: "#854d0e" },
  DELIVERED:        { bg: "#dcfce7", color: "#166534" },
  CANCELLED:        { bg: "#fee2e2", color: "#991b1b" },
};

function getStepIndex(status) {
  if (!status) return 0;
  const norm = status.toUpperCase();
  if (norm === "CANCELLED" || norm === "SHIPROCKET_FAILED") return -1;
  if (norm === "PENDING") return 0;
  if (norm === "CONFIRMED") return 1;
  const idx = STATUS_STEPS.findIndex((s) => s.key === norm);
  return idx === -1 ? 0 : idx;
}

function AminiiTrackOrder() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const initialOrderId = searchParams.get("orderId") || "";

  const [inputOrderId, setInputOrderId] = useState(initialOrderId);
  const [trackingData, setTrackingData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchTracking = async (idToFetch) => {
    const id = (idToFetch || "").toString().trim();
    if (!id) {
      setError("Please enter a valid Order ID.");
      return;
    }
    setLoading(true);
    setError("");
    setTrackingData(null);
    try {
      const response = await axios.get(`${API_BASE_URL}/track?orderId=${id}`);
      const data = response.data?.data || response.data;
      if (data && data.orderId) {
        setTrackingData(data);
      } else {
        setError("No tracking information found for this Order ID.");
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "Unable to track order. Please check the Order ID."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (initialOrderId) {
      fetchTracking(initialOrderId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialOrderId]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchTracking(inputOrderId);
  };

  const status = (trackingData?.orderStatus || "ORDER_PLACED").toUpperCase();
  const isCancelled = status === "CANCELLED" || status === "SHIPROCKET_FAILED";
  const currentStepIdx = trackingData ? getStepIndex(status) : -1;
  const currentStep = STATUS_STEPS[currentStepIdx] || STATUS_STEPS[0];
  const statusStyle = STATUS_COLORS[status] || { bg: "#f1f5f9", color: "#334155" };

  const awbCode = trackingData?.awbCode || trackingData?.shiprocket?.awbCode;
  const courierName = trackingData?.courierName || trackingData?.shiprocket?.courierName;
  const trackingUrl =
    trackingData?.trackingUrl ||
    trackingData?.shiprocket?.trackingUrl ||
    (awbCode ? `https://shiprocket.co/tracking/${awbCode}` : null);

  const hasShipping = !!(awbCode || courierName || trackingUrl);

  return (
    <div className="aminii-track-page">
      <AminiiNavbar />

      <main className="track-container">
        {/* Back button */}
        <button className="track-back-btn" onClick={() => navigate("/orders")}>
          ← My Orders
        </button>

        {/* Search Card */}
        <div className="track-header-card">
          <h2>Track Your Order</h2>
          <p>Enter your Order ID to check the current status of your shipment</p>
          <form onSubmit={handleSearch} className="track-search-form">
            <input
              type="number"
              placeholder="Enter Order ID  e.g. 10042"
              value={inputOrderId}
              onChange={(e) => setInputOrderId(e.target.value)}
              className="track-input"
              required
            />
            <button type="submit" className="track-button" disabled={loading}>
              {loading ? "Searching…" : "Track Order"}
            </button>
          </form>
        </div>

        {error && <div className="track-error-card">{error}</div>}

        {loading && (
          <div className="track-skeleton-card">
            <div className="track-skel track-skel-title" />
            <div className="track-skel track-skel-bar" />
            <div className="track-skel track-skel-bar short" />
          </div>
        )}

        {!loading && trackingData && (
          <>
            {/* ── Summary Row ── */}
            <div className="track-results-card">
              <div className="track-summary-row">
                <div className="track-order-id-block">
                  <span className="tsum-label">Order</span>
                  <span className="tsum-val">#{trackingData.orderId}</span>
                </div>
                <div
                  className="tsum-badge"
                  style={{ background: statusStyle.bg, color: statusStyle.color }}
                >
                  {isCancelled ? "✕ Cancelled" : currentStep?.icon + " " + (currentStep?.label || status.replace(/_/g, " "))}
                </div>
              </div>

              {/* ── Status Description ── */}
              {!isCancelled && currentStep && (
                <div className="track-status-desc">
                  {currentStep.desc}
                </div>
              )}

              {isCancelled && (
                <div className="track-cancelled-notice">
                  This order has been cancelled.
                  {trackingData.cancellationReason && (
                    <span> Reason: {trackingData.cancellationReason}</span>
                  )}
                </div>
              )}

              {/* ── Progress Stepper ── */}
              {!isCancelled && (
                <div className="stepper-wrapper">
                  {/* Progress line fill */}
                  <div
                    className="stepper-progress-line"
                    style={{
                      width:
                        currentStepIdx <= 0
                          ? "0%"
                          : `${(currentStepIdx / (STATUS_STEPS.length - 1)) * 100}%`,
                    }}
                  />
                  {STATUS_STEPS.map((step, idx) => {
                    const isCompleted = currentStepIdx > idx;
                    const isCurrent = currentStepIdx === idx;
                    return (
                      <div
                        key={step.key}
                        className={`step-item ${isCompleted ? "completed" : ""} ${isCurrent ? "active" : ""}`}
                      >
                        <div className="step-circle">
                          {isCompleted ? "✓" : isCurrent ? step.icon : idx + 1}
                        </div>
                        <div className="step-label">{step.label}</div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* ── Shiprocket / Courier Info ── */}
              {hasShipping ? (
                <div className="courier-details-box">
                  <div className="courier-box-title">Shipment Details</div>
                  <div className="courier-grid">
                    {courierName && (
                      <div className="courier-row">
                        <span className="cour-label">Courier Partner</span>
                        <strong className="cour-val">{courierName}</strong>
                      </div>
                    )}
                    {awbCode && (
                      <div className="courier-row">
                        <span className="cour-label">AWB / Tracking No.</span>
                        <strong className="cour-val cour-awb">{awbCode}</strong>
                      </div>
                    )}
                  </div>
                  {trackingUrl && (
                    <a
                      href={trackingUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-track-external"
                    >
                       Track Live on Courier Website ↗
                    </a>
                  )}
                </div>
              ) : (
                !isCancelled && (
                  <div className="courier-details-box courier-not-shipped">
                    {/* <div className="courier-not-shipped-icon">📦</div> */}
                    <div className="courier-not-shipped-text">
                      <strong>Not yet dispatched</strong>
                      <p>
                        Courier tracking details will appear here once your order is
                        picked up by our delivery partner. You'll also receive an SMS
                        and email when it ships.
                      </p>
                    </div>
                  </div>
                )
              )}
            </div>
          </>
        )}
      </main>

      <AminiiFooter />
    </div>
  );
}

export default AminiiTrackOrder;
